import { useEffect } from "react";
import { apiPut, hasAimgToken } from "@/shared/api/client";

/** 閲覧継続を通知する間隔。 */
export const ONLINE_USERS_HEARTBEAT_INTERVAL_MS = 60_000;
const HEARTBEAT_LOCK_NAME = "nijiurachan-online-users-heartbeat";

/**
 * 表示中の代表タブから閲覧継続を通知する。
 *
 * 同接数は旧板 (20分窓のユニークclientIP) に合わせてIPベースの在席記録で数えるため、
 * tokenの有無に関わらず送る。tokenがあれば添えて送り、従来どおりlast_used_atの
 * 前進 (トークン失効の回避) も兼ねる。
 */
export function useOnlineUsersHeartbeat(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    let activeController: AbortController | null = null;

    const stop = (): void => {
      activeController?.abort();
      activeController = null;
    };
    const start = (): void => {
      if (document.visibilityState !== "visible" || activeController) return;

      const controller = new AbortController();
      activeController = controller;
      void runAsRepresentativeTab(controller.signal).finally(() => {
        if (activeController === controller) activeController = null;
      });
    };
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        start();
      } else {
        stop();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    start();

    return (): void => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stop();
    };
  }, [enabled]);
}

async function runAsRepresentativeTab(signal: AbortSignal): Promise<void> {
  // token未発行なら匿名で送る (送信時点ごとに判定し、発行後は自動でtokenが載る)
  const sendHeartbeat = async (): Promise<void> =>
    apiPut<void>(
      "/online-users/heartbeat",
      hasAimgToken()
        ? {
            requiresToken: true,
            allowTokenIssue: false,
            cache: "no-store",
            signal,
          }
        : { cache: "no-store", signal },
    );
  const lockManager: LockManager | undefined = navigator.locks;
  if (!lockManager) {
    await runOnlineUsersHeartbeat(signal, sendHeartbeat);
    return;
  }

  try {
    await lockManager.request(
      HEARTBEAT_LOCK_NAME,
      { mode: "exclusive", signal },
      async (): Promise<void> => runOnlineUsersHeartbeat(signal, sendHeartbeat),
    );
  } catch {
    // 古いbrowser等でWeb Locksが使えなくても、server側の重複排除は維持する。
    if (!signal.aborted) await runOnlineUsersHeartbeat(signal, sendHeartbeat);
  }
}

/** 即時送信後、失敗時も再試行を挟まず一定間隔でheartbeatを送る。 */
export function runOnlineUsersHeartbeat(
  signal: AbortSignal,
  sendHeartbeat: () => Promise<void>,
): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    let sending = false;
    const send = (): void => {
      if (sending || signal.aborted) return;
      sending = true;
      void sendHeartbeat()
        .catch(() => undefined)
        .finally(() => {
          sending = false;
        });
    };

    const intervalId = window.setInterval(
      send,
      ONLINE_USERS_HEARTBEAT_INTERVAL_MS,
    );
    const stop = (): void => {
      window.clearInterval(intervalId);
      signal.removeEventListener("abort", stop);
      resolve();
    };

    signal.addEventListener("abort", stop, { once: true });
    send();
  });
}
